// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file TextArea.tsx
 * @input Uses React, useId, ChangeEvent, ClipboardEvent, FocusEvent, Field, Icon, Spinner
 * @output Exports TextArea component, TextAreaProps, TextAreaStatus, TextAreaStatusType
 * @position Core implementation; consumed by index.ts, tested by TextArea.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/TextArea/TextArea.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/TextArea/TextArea.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/TextArea/index.ts (exports if types change)
 * - /apps/storybook/stories/TextArea.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/TextArea/ (showcase blocks)
 */
import { useId, useOptimistic, useTransition, useRef, useCallback, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { Field, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { Spinner } from "../Spinner/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { useInputContainer } from "../hooks/useInputContainer.js";
import { useInputStatusIcon } from "../hooks/useInputStatusIcon.js";
import { useAnnounce } from "../hooks/useAnnounce.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { themeProps } from "../utils/themeProps.js";
import { characterCount } from "../utils/characters.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const COUNTER_WARNING_THRESHOLD = 0.8;
const styles = {
  wrapper: {
    kY2c9j: "x1vjfegm",
    k1xSpc: "x1lliihq",
    k8WAf4: "xt970qd",
    kg3NbH: "xnjsko4",
    "--_textarea-inline-padding": "x1kdpo6e",
    $$css: true
  },
  textarea: {
    k1xSpc: "x1lliihq",
    kzqmXN: "xh8yej3",
    kB7OPa: "x9f619",
    k7Eaqz: "xeuugli",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    k8WAf4: "xu0wf1k",
    kg3NbH: "x1w6f3ag",
    kMv6JI: "x9ynric",
    kGuDYH: "xjm74w1 x6pjikd",
    kLWn49: "xw6l6zx",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w",
    kI3sdo: "x1a2a7pz",
    k8Qsv1: "xeyghm5",
    kHenm0: "x288g5",
    $$css: true
  },
  textareaDisabled: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  textareaWithStartIcon: {
    kZCmMZ: "x1b6te9z",
    $$css: true
  },
  textareaWithStatus: {
    kwRFfy: "xp2dy4z",
    $$css: true
  },
  textareaWithBusyStatus: {
    kwRFfy: "x1g2apga",
    $$css: true
  },
  textareaWithCounter: {
    kGO01o: "xekm6fs",
    $$css: true
  }
};
const textareaSizeStyles = {
  sm: {
    $$css: true
  },
  md: {
    $$css: true
  },
  lg: {
    k8WAf4: "xce4md1",
    $$css: true
  }
};
/**
 * A multi-line text input component for collecting longer user input.
 *
 * @example
 * ```
 * <TextArea label="Description" value={description} onChange={setDescription} />
 * <TextArea label="Notes" rows={5} value={notes} onChange={setNotes} />
 * ```
 */
export function TextArea({
  label,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  onChange,
  changeAction,
  isLoading = false,
  value,
  placeholder,
  rows = 3,
  isDisabled = false,
  isReadOnly = false,
  disabledMessage,
  status,
  statusVariant = 'attached',
  labelTooltip,
  startIcon,
  hasSpellCheck = true,
  onPaste,
  maxLength,
  hasAutoFocus = false,
  size: sizeProp,
  htmlName,
  onFocus,
  onBlur,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const size = useSize(sizeProp, 'md');
  const t = useTranslator();
  const announce = useAnnounce();
  const id = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const counterID = useId();
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  // Tracks the counter "zone" last announced (under / near / over) so we only
  // announce on a zone change, not on every keystroke.
  const counterZoneRef = useRef(null);
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the textarea container (which already exists)
  // and the textarea stays perceivable via aria-disabled instead of the native
  // disabled attribute. The field is made read-only so it can't be typed into,
  // and value mutation is blocked by the isDisabled guard in handleChange.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the textarea, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const {
    statusIcon,
    describedBy: statusTooltipDescribedBy
  } = useInputStatusIcon({
    status,
    statusVariant
  });
  const ariaDescribedBy = [description ? descriptionID : null, statusVariant !== 'tooltip' && status?.message ? statusMessageID : null,
  // The tooltip variant renders no message box; describe the input by the
  // tooltip's content instead so the status is still announced.
  statusTooltipDescribedBy, maxLength != null ? counterID : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null].filter(Boolean).join(' ') || undefined;

  // Announce the character-count status through the shared live region, but
  // only when the value crosses into a new zone (near the limit / over the
  // limit) so we don't speak on every keystroke. Over-limit is assertive (an
  // error the user should hear immediately); nearing the limit is polite.
  const announceCounter = useCallback(length => {
    if (maxLength == null) {
      return;
    }
    const zone = length > maxLength ? 'over' : length >= maxLength * COUNTER_WARNING_THRESHOLD ? 'near' : 'under';
    if (zone === counterZoneRef.current) {
      return;
    }
    counterZoneRef.current = zone;
    if (zone === 'over') {
      announce(t('@astryx.textArea.charactersOverLimit', {
        count: length - maxLength
      }), 'assertive');
    } else if (zone === 'near') {
      announce(t('@astryx.textArea.charactersRemaining', {
        count: maxLength - length
      }));
    }
  }, [announce, t, maxLength]);
  const handleChange = e => {
    // Value can't change while showing a disabled message (the field is
    // read-only and non-native-disabled), but guard the handler too so the
    // optimistic value and callbacks never fire.
    if (isDisabled || isReadOnly) {
      return;
    }
    const newValue = e.target.value;
    // Guarded here, not just inside announceCounter, so textareas without a
    // maxLength never pay for segmenting the whole value on each keystroke.
    if (maxLength != null) {
      announceCounter(characterCount(newValue));
    }
    onChange?.(newValue, e);
    if (changeAction && !e.defaultPrevented) {
      startTransition(async () => {
        setOptimisticValue(newValue);
        await changeAction(newValue, e);
      });
    }
  };

  // Counter semantics count user-perceived characters, so an emoji or flag
  // sequence counts as one character, not its code units.
  // Only measured when a counter exists — segmentation is O(value length),
  // and memoized so re-renders that keep the same value skip it entirely.
  const valueLength = useMemo(() => maxLength != null ? characterCount(optimisticValue) : 0, [maxLength, optimisticValue]);
  const effectivelyDisabled = isDisabled || isBusy;

  // Focus textarea when clicking anywhere on the wrapper (icons, padding, etc.)
  const {
    onClick: handleWrapperClick,
    onMouseUp: handleWrapperMouseUp
  } = useInputContainer({
    containerRef,
    inputRef: textareaRef,
    disabled: effectivelyDisabled
  });
  return /*#__PURE__*/_jsxs(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: id,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    statusVariant: statusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        containerRef.current = el;
        // Anchor + hover/focus listeners for the disabled-message tooltip.
        // Handlers are gated internally by isEnabled, and anchor names
        // compose, so attaching unconditionally is safe.
        disabledMessageTooltip.ref(el);
      },
      onClick: handleWrapperClick,
      onMouseUp: handleWrapperMouseUp,
      ...mergeProps(themeProps('textarea', {
        size,
        status: status?.type ?? null,
        disabled: isDisabled ? 'disabled' : null,
        readonly: isReadOnly ? 'readonly' : null
      }), stylex.props(inputWrapperStyles.base, styles.wrapper, isDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && !isDisabled && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type], xstyle), className, style),
      children: [startIcon && /*#__PURE__*/_jsx("span", {
        ...{
          className: "x10l6tqk xctzyg x1kiyvbf x47corl x78zum5"
        },
        children: renderIconSlot(startIcon, {
          size: 'sm',
          color: 'secondary'
        })
      }), /*#__PURE__*/_jsx("textarea", {
        ...rest,
        ref: mergeRefs(ref, textareaRef),
        id: id,
        name: isDisabled ? undefined : htmlName,
        value: optimisticValue,
        onChange: handleChange,
        onPaste: onPaste,
        onFocus: onFocus,
        onBlur: onBlur,
        placeholder: placeholder,
        rows: rows
        // With a disabledMessage the textarea keeps focusability via
        // aria-disabled so the reason is focus-discoverable; readOnly + the
        // handleChange guard keep the value from changing.
        ,
        disabled: isDisabled && !showsDisabledMessage,
        "aria-disabled": showsDisabledMessage ? 'true' : undefined,
        readOnly: isReadOnly || showsDisabledMessage || undefined,
        spellCheck: hasSpellCheck,
        autoFocus: hasAutoFocus,
        "data-autofocus": hasAutoFocus || undefined,
        "aria-describedby": ariaDescribedBy,
        "aria-required": isRequired && !isOptional ? 'true' : undefined,
        "aria-invalid": status?.type === 'error' || maxLength != null && valueLength > maxLength ? 'true' : undefined,
        "aria-busy": isBusy || undefined,
        ...stylex.props(styles.textarea, textareaSizeStyles[size], isDisabled && styles.textareaDisabled, Boolean(startIcon) && styles.textareaWithStartIcon,
        // Reserve trailing space only when the end slot actually renders
        // something (spinner or on-field status icon). The `detached`
        // status variant suppresses the on-field icon — its glyph lives in
        // the message box below — so reserving here would inset the text
        // for an icon that never appears.
        (isBusy || statusIcon != null) && styles.textareaWithStatus, isBusy && statusIcon != null && styles.textareaWithBusyStatus, maxLength != null && styles.textareaWithCounter)
      }), (isBusy || statusIcon) && /*#__PURE__*/_jsxs("span", {
        ...{
          className: "x10l6tqk xctzyg x13oyzgk x47corl x78zum5 x6s0dn4 x1txdalj"
        },
        children: [isBusy && /*#__PURE__*/_jsx(Spinner, {
          size: "sm"
        }), statusIcon]
      }), maxLength != null && /*#__PURE__*/_jsxs("div", {
        id: counterID,
        ...{
          0: {
            className: "x10l6tqk x112dmjn x13oyzgk x47corl x78zum5 x6s0dn4 xzye2dw x9ynric x141an7d x1ltkj2j xv1l7n4"
          },
          1: {
            className: "x10l6tqk x112dmjn x13oyzgk x47corl x78zum5 x6s0dn4 xzye2dw x9ynric x141an7d x1ltkj2j xjt36v0"
          }
        }[!!(valueLength > maxLength) << 0],
        children: [valueLength > maxLength &&
        /*#__PURE__*/
        // Non-color cue so the over-limit state isn't conveyed by the red
        // color alone (WCAG 1.4.1). Decorative — the count text and the
        // live-region announcement carry the meaning.
        _jsx(Icon, {
          icon: "warning",
          size: "sm"
        }), valueLength, "/", maxLength]
      })]
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
TextArea.displayName = 'TextArea';